import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Spin, Tooltip } from "antd";
import {
  LuChevronLeft,
  LuChevronRight,
  LuDownload,
  LuFileWarning,
  LuFlipHorizontal2,
  LuFlipVertical2,
  LuRotateCcw,
  LuRotateCw,
  LuX,
  LuZoomIn,
  LuZoomOut,
} from "react-icons/lu";
import CustomButton from "./button";
import cn from "../../utils/cn";

// Neutral transform state for an image slide. offsetX/offsetY (screen px) let us
// zoom toward the cursor instead of always toward the center.
const INITIAL_TRANSFORM = { scale: 1, rotate: 0, flipX: 1, flipY: 1, offsetX: 0, offsetY: 0 };
const MIN_SCALE = 1;
const MAX_SCALE = 5;
const SCALE_STEP = 0.5; // toolbar button step
const WHEEL_STEP = 0.4; // scroll step — larger = faster zoom
const WHEEL_THROTTLE_MS = 30; // lower = more responsive scroll

const clampScale = s => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

// How long a slide must stay visible before its neighbours are prefetched. Long
// enough that a glance-and-close, or paging straight through, costs nothing.
const PREFETCH_DELAY_MS = 500;

// Never prefetch on a metered or slow connection — the user is paying for it and
// the win (instant paging) isn't worth the cost there. The Network Information
// API is Chromium-only, so absence means "no reason to hold back".
const shouldSkipPrefetch = () => {
  const conn = typeof navigator !== "undefined" ? navigator.connection : null;
  if (!conn) return false;
  return !!conn.saveData || ["slow-2g", "2g"].includes(conn.effectiveType);
};

// Builds the CSS transform. Translate is outermost (screen px) so the cursor-zoom
// math stays in screen space; rotate/scale/flip apply to the image itself.
const toTransform = ({ scale, rotate, flipX, flipY, offsetX, offsetY }) =>
  `translate(${offsetX}px, ${offsetY}px) rotate(${rotate}deg) scale(${scale}) scaleX(${flipX}) scaleY(${flipY})`;

/**
 * Media types this previewer understands. Callers map their own enums to these.
 */
export const MEDIA_PREVIEW_TYPE = {
  IMAGE: "image",
  VIDEO: "video",
  DOC: "doc",
};

// A <video>'s failure carries a MediaError; map its code to plain language.
// https://developer.mozilla.org/en-US/docs/Web/API/MediaError/code
const VIDEO_ERROR_REASONS = {
  1: "Playback was aborted.",
  2: "A network error interrupted the download.",
  3: "The file is corrupt, or uses a codec this browser can't decode.",
  4: "The format isn't supported, or the file couldn't be fetched.",
};

const describeMediaError = event => {
  const media = event?.target;
  const code = media?.error?.code;
  if (code && VIDEO_ERROR_REASONS[code]) return VIDEO_ERROR_REASONS[code];
  // <img> gives no detail at all — the browser only reports "it didn't load".
  return "The browser couldn't load this file.";
};

// Follows up a failure with a HEAD request, for the console only — a 403
// (expired link) and a 404 (deleted file) look identical to an <img>, and that
// distinction helps when debugging. Deliberately not surfaced in the UI: a
// status line is meaningless to the user, and often reads as a contradiction
// (a 200 next to "can't be previewed" when the block is client-side, e.g. CORB).
const probeUrl = async url => {
  try {
    const res = await fetch(url, { method: "HEAD" });
    if (res.ok) {
      // Reachable but unrenderable — usually the wrong Content-Type on upload.
      return `Server returned ${res.status} (${
        res.headers.get("content-type") || "unknown type"
      }).`;
    }
    return `Server returned ${res.status} ${res.statusText || ""}`.trim();
  } catch {
    return "The file couldn't be reached (network, CORS, or blocked request).";
  }
};

// Shown when a file is missing, corrupt, or the host refuses it — so a broken
// item reads as a clear message instead of a black stage or a broken-image icon.
const UnavailableState = ({ name, url, reason }) => (
  <div className="flex flex-col items-center gap-3 px-6 text-center">
    <LuFileWarning className="text-3xl text-white/50" />
    <div className="text-sm font-medium text-white/90">This file can&apos;t be previewed</div>
    {!!name && <div className="max-w-md break-all text-xs text-white/60">{name}</div>}
    {/* Plain-language cause only. Anything technical goes to the console. */}
    {!!reason && <div className="max-w-md text-xs text-white/60">{reason}</div>}
    {url && (
      // Last resort: let the browser try the file directly.
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs font-medium text-white/80 underline hover:text-white"
      >
        Open in a new tab
      </a>
    )}
  </div>
);

/**
 * Render a single media item by type, sized to fill the stage. New types get
 * added here only.
 *
 * `failure` ({ reason, detail }) flips the slide to the unavailable state;
 * `onFail` is what the media element calls when the browser can't decode or
 * fetch it.
 */
const renderSlide = (item, autoPlay, transform, failure, onFail, onLoaded, videoRef) => {
  if (!item?.url) {
    return <div className="text-white/80 text-sm">No preview available</div>;
  }
  if (failure) {
    return <UnavailableState name={item.name} url={item.url} reason={failure.reason} />;
  }
  switch (item.type) {
    case MEDIA_PREVIEW_TYPE.VIDEO:
      return (
        // src on the element (not a <source> child) so onError actually fires —
        // a failing <source> fires on itself and doesn't bubble.
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
          key={item.url}
          ref={videoRef}
          src={item.url}
          className="max-h-full max-w-full rounded"
          controls
          autoPlay={autoPlay}
          onError={onFail}
        />
      );
    case MEDIA_PREVIEW_TYPE.DOC:
      return (
        // An <iframe> here, deliberately — NOT <object type="application/pdf">.
        // Chrome's CORB blocks a cross-origin PDF requested as a subresource
        // (which is how <object> with an explicit plugin type fetches it), so
        // the document never reaches the viewer. An iframe's src is a frame
        // *navigation* instead, which CORB doesn't apply to, so the browser's
        // built-in PDF viewer loads it — the same way the old ViewMediaPopup did.
        //
        // self-stretch: the stage centers its child, and an iframe has no
        // intrinsic height, so it must be told to fill the row or it collapses.
        //
        // No onError: a cross-origin iframe reports nothing useful on failure and
        // can fire for subresources, which would wrongly flag a good PDF.
        <iframe
          key={item.url}
          title={item.name || "document"}
          src={item.url}
          className="h-full min-h-0 w-full self-stretch rounded bg-white"
          allowFullScreen
        />
      );
    case MEDIA_PREVIEW_TYPE.IMAGE:
    default:
      return (
        <img
          key={item.url}
          src={item.url}
          alt={item.name || "media"}
          draggable={false}
          onError={onFail}
          onLoad={onLoaded}
          // The visible slide is the most important request on the page — opt it
          // out of the browser's lazy/low-priority heuristics.
          fetchPriority="high"
          decoding="async"
          style={{
            transform: toTransform(transform),
            transition: "transform 0.12s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
          className="max-h-full max-w-full select-none rounded object-contain"
        />
      );
  }
};

// Circular nav button — sits on the sides, drops to the bottom corners on small
// screens so it never covers native video / iframe controls.
// Header transform-toolbar button (zoom / rotate / flip) — icon-only, brightens
// on hover with a subtle chip behind it.
const TOOLBAR_BTN = cn(
  "flex items-center justify-center !border-0 !bg-transparent !text-lg !text-white/70",
  "hover:!bg-white/10 hover:!text-white"
);

const NAV_BTN = cn(
  "absolute top-1/2 z-20 flex !h-10 !w-10 -translate-y-1/2 items-center justify-center",
  "!rounded-full !border-0 !bg-white/15 !text-lg !text-white backdrop-blur hover:!bg-white/25",
  // On mobile / tablet, drop the arrows below the media (clear of native video
  // controls) and pin them near the bottom edge.
  "max-[1024px]:top-auto max-[1024px]:bottom-1 max-[1024px]:translate-y-0"
);

/**
 * MediaPreview — a standalone, scalable media previewer. Handles a single item
 * or a set (carousel), and image / video / doc types. Full-screen framed
 * "blackish glass" lightbox, responsive, matching the media-gallery look.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {Array<{ url: string, type: string, name?: string }>} props.items
 * @param {number} [props.initialIndex]  Slide to open on (default 0).
 * @param {string} [props.title]         Optional header title.
 * @param {boolean} [props.autoPlay]     Auto-start video playback (default false).
 * @param {boolean} [props.showDownload] Show the download action (default true).
 * @param {(item: object) => void} [props.onDownload] Takes over the download
 *   action — for callers that download through a helper (blob fetch) and/or need
 *   to record the download. Without it, the plain `<a download>` is used.
 * @param {(index: number, item: object) => void} [props.onIndexChange] Fires on
 *   open and on every slide change — for callers that record which item was
 *   viewed.
 * @param {(el: HTMLVideoElement|null, item: object) => (void|Function)}
 *   [props.onVideoMount] Called with the <video> element when a video slide
 *   mounts (and null on unmount) — for callers that attach playback observers.
 *   A returned cleanup function is run when the slide goes away.
 */
const MediaPreview = ({
  open,
  onClose,
  items = [],
  initialIndex = 0,
  title,
  autoPlay = false,
  showDownload = true,
  onDownload,
  onIndexChange,
  onVideoMount,
}) => {
  const list = (Array.isArray(items) ? items : []).filter(it => it?.url);
  const count = list.length;
  const [index, setIndex] = useState(initialIndex);
  const [transform, setTransform] = useState(INITIAL_TRANSFORM);
  // A caller-owned download fetches the file as a blob, which takes long enough
  // to need feedback — the icon becomes a spinner until it settles.
  const [downloading, setDownloading] = useState(false);
  // URLs the browser couldn't load → why. Keyed by URL rather than index so a
  // failure sticks to the right file when the set or the order changes.
  const [failures, setFailures] = useState(() => ({}));
  // URLs that have finished decoding, so the spinner only covers a real wait
  // (a cached image fires onLoad immediately and never shows one).
  const [loadedUrls, setLoadedUrls] = useState(() => new Set());

  // Clamp/reset the active slide whenever the previewer (re)opens or the set
  // changes, so a stale index never points past the list.
  useEffect(() => {
    if (open) setIndex(Math.min(Math.max(0, initialIndex), Math.max(0, count - 1)));
  }, [open, initialIndex, count]);

  const current = list[index];
  const hasMultiple = count > 1;
  const failure = current?.url ? failures[current.url] : null;
  const hasFailed = !!failure;
  // Only images report load progress here; video and PDF have their own UI.
  const isLoading =
    !!current?.url &&
    current.type === MEDIA_PREVIEW_TYPE.IMAGE &&
    !hasFailed &&
    !loadedUrls.has(current.url);

  const markCurrentLoaded = () => {
    const url = current?.url;
    if (!url) return;
    setLoadedUrls(prev => (prev.has(url) ? prev : new Set(prev).add(url)));
  };
  // A broken slide has nothing to zoom or rotate, so hide those controls.
  const isImage = current?.type === MEDIA_PREVIEW_TYPE.IMAGE && !hasFailed;

  // Records the failure, then probes the URL to fill in the specific cause.
  const markCurrentFailed = event => {
    const url = current?.url;
    if (!url || failures[url]) return;
    const reason = describeMediaError(event);
    setFailures(prev => ({ ...prev, [url]: { reason } }));
    // eslint-disable-next-line no-console
    console.warn("[MediaPreview] failed to load", { url, type: current?.type, reason });
    // Console-only: no state update, so the probe can't change what's rendered.
    probeUrl(url).then(detail => {
      // eslint-disable-next-line no-console
      console.warn("[MediaPreview] probe result", { url, detail });
    });
  };

  // Warm the neighbouring slides while the user looks at this one, so paging
  // through the carousel hits the browser cache instead of the network. Only
  // images — videos and PDFs are large and stream on demand.
  //
  // Kept free rather than merely cheap:
  //  - single-item previews never prefetch (nothing to page to),
  //  - it waits out PREFETCH_DELAY_MS, so opening and closing straight away, or
  //    paging quickly past a slide, cancels before any request is made,
  //  - it respects Data Saver and 2g connections.
  useEffect(() => {
    if (!open || count < 2 || shouldSkipPrefetch()) return undefined;
    const timer = setTimeout(() => {
      [index - 1, index + 1].forEach(i => {
        const neighbour = list[(i + count) % count];
        if (!neighbour?.url || neighbour.type !== MEDIA_PREVIEW_TYPE.IMAGE) return;
        const img = new Image();
        img.decoding = "async";
        img.fetchPriority = "low";
        img.src = neighbour.url;
      });
    }, PREFETCH_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index, count]);

  // Report the visible slide while open. Keyed on the item too, so a caller
  // recording views doesn't miss one when the set changes under the same index.
  useEffect(() => {
    if (open && current) onIndexChange?.(index, current);
    // onIndexChange is intentionally not a dep — callers commonly pass an inline
    // closure, which would re-fire this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index, current?.url]);

  // Reset zoom / rotate / flip whenever the active slide or open state changes,
  // so a transformed image never carries over to the next one.
  useEffect(() => {
    setTransform(INITIAL_TRANSFORM);
  }, [index, open]);

  const stageRef = useRef(null);
  const wheelLockRef = useRef(false);

  // Hands the <video> element to the caller and runs whatever teardown it
  // returns, so an observer attached to one slide is detached with it.
  //
  // This ref callback MUST stay stable across renders. React invokes a changed
  // ref callback with null and then the element on every render, which would
  // detach and re-attach the caller's observer each time — and an observer that
  // accumulates (watch time) loses its total when re-created. So:
  //  - the handler is read through a ref, so a caller's inline arrow doesn't
  //    change our identity,
  //  - the callback is keyed on the slide URL only, so it re-attaches exactly
  //    when the video actually changes.
  const videoCleanupRef = useRef(null);
  const videoElRef = useRef(null);
  const onVideoMountRef = useRef(onVideoMount);
  const currentItemRef = useRef(current);
  useEffect(() => {
    onVideoMountRef.current = onVideoMount;
    currentItemRef.current = current;
  });

  // Removing a <video> from the DOM doesn't reliably stop playback — the browser
  // may keep decoding audio until the element is collected, so a closed preview
  // can still be heard. Pause explicitly, and do it before the caller's teardown
  // so the resulting `pause` event is still observed (watch time gets recorded).
  const stopVideo = () => {
    const el = videoElRef.current;
    if (el && !el.paused) {
      try {
        el.pause();
      } catch {
        // A detached element can reject pause(); nothing to recover from.
      }
    }
  };

  const detachVideo = () => {
    stopVideo();
    if (videoCleanupRef.current) {
      videoCleanupRef.current();
      videoCleanupRef.current = null;
    }
    videoElRef.current = null;
  };

  const currentUrl = current?.url;
  const videoRef = useCallback(
    el => {
      detachVideo();
      const handler = onVideoMountRef.current;
      if (!el) return;
      videoElRef.current = el;
      if (!handler) return;
      const cleanup = handler(el, currentItemRef.current);
      videoCleanupRef.current = typeof cleanup === "function" ? cleanup : null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUrl]
  );

  // Closing stops playback. `destroyOnClose` on the Modal unmounts the stage,
  // which triggers this too, but the explicit pause covers the window before
  // React gets to the unmount.
  useEffect(() => {
    if (!open) detachVideo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Unmounting the whole previewer (route change, parent conditional) has no ref
  // callback change to hang teardown off, so detach here as well.
  useEffect(
    () => () => detachVideo(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const go = dir => setIndex(prev => (prev + dir + count) % count);

  // Applies a scale delta, keeping the point at (cx, cy) — screen px relative to
  // the stage center — fixed under the cursor. cx/cy default to 0 (center zoom).
  const applyZoom = (delta, cx = 0, cy = 0) =>
    setTransform(t => {
      const nextScale = clampScale(t.scale + delta);
      if (nextScale === t.scale) return t;
      // Back to 1× — recenter so the image never drifts off after zooming out.
      if (nextScale <= MIN_SCALE) return { ...t, scale: MIN_SCALE, offsetX: 0, offsetY: 0 };
      return {
        ...t,
        scale: nextScale,
        offsetX: cx - ((cx - t.offsetX) / t.scale) * nextScale,
        offsetY: cy - ((cy - t.offsetY) / t.scale) * nextScale,
      };
    });

  // Awaits the caller's handler whether it's sync or returns a promise, and
  // always clears the spinner — a failed download must not leave it stuck.
  const handleDownloadClick = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await onDownload(current);
    } finally {
      setDownloading(false);
    }
  };

  const rotate = deg => setTransform(t => ({ ...t, rotate: t.rotate + deg }));
  const flip = axis =>
    setTransform(t => (axis === "x" ? { ...t, flipX: -t.flipX } : { ...t, flipY: -t.flipY }));

  // Scroll-to-zoom on image slides — throttled, and zooms toward the cursor.
  const handleWheel = e => {
    if (!isImage || wheelLockRef.current) return;
    wheelLockRef.current = true;
    setTimeout(() => {
      wheelLockRef.current = false;
    }, WHEEL_THROTTLE_MS);
    const rect = stageRef.current?.getBoundingClientRect();
    const cx = rect ? e.clientX - (rect.left + rect.width / 2) : 0;
    const cy = rect ? e.clientY - (rect.top + rect.height / 2) : 0;
    applyZoom((e.deltaY < 0 ? 1 : -1) * WHEEL_STEP, cx, cy);
  };

  // Zoom / rotate / flip controls (images only). Rendered in the header on
  // desktop and as a floating bar over the media on mobile / tablet, so the lead
  // name always keeps its space in the header.
  const transformToolbar = isImage ? (
    <div className="flex items-center gap-0.5 rounded-full border border-solid border-white/10 bg-black/40 px-1 py-1 backdrop-blur sm:px-1.5">
      <CustomButton
        type="text"
        shape="circle"
        tooltipTitle="Zoom out"
        disabled={transform.scale <= MIN_SCALE}
        icon={<LuZoomOut />}
        className={TOOLBAR_BTN}
        handleClick={() => applyZoom(-SCALE_STEP)}
      />
      <CustomButton
        type="text"
        shape="circle"
        tooltipTitle="Zoom in"
        disabled={transform.scale >= MAX_SCALE}
        icon={<LuZoomIn />}
        className={TOOLBAR_BTN}
        handleClick={() => applyZoom(SCALE_STEP)}
      />
      <CustomButton
        type="text"
        shape="circle"
        tooltipTitle="Rotate left"
        icon={<LuRotateCcw />}
        className={TOOLBAR_BTN}
        handleClick={() => rotate(-90)}
      />
      <CustomButton
        type="text"
        shape="circle"
        tooltipTitle="Rotate right"
        icon={<LuRotateCw />}
        className={TOOLBAR_BTN}
        handleClick={() => rotate(90)}
      />
      <CustomButton
        type="text"
        shape="circle"
        tooltipTitle="Flip horizontal"
        icon={<LuFlipHorizontal2 />}
        className={TOOLBAR_BTN}
        handleClick={() => flip("x")}
      />
      <CustomButton
        type="text"
        shape="circle"
        tooltipTitle="Flip vertical"
        icon={<LuFlipVertical2 />}
        className={TOOLBAR_BTN}
        handleClick={() => flip("y")}
      />
    </div>
  ) : null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width="96vw"
      closable={false}
      // Without this the Modal keeps its children mounted after closing, so the
      // <video> (and its buffered stream) survives in the background. Also means
      // the next open starts clean instead of on the previous slide's state.
      destroyOnClose
      styles={{
        // Solid dark lightbox panel + dark backdrop.
        content: {
          background: "#141414",
          borderRadius: 14,
          boxShadow: "0 24px 70px rgba(0, 0, 0, 0.55)",
          padding: 0,
          overflow: "hidden",
          height: "90vh",
        },
        body: { padding: 0, height: "90vh" },
        mask: { background: "rgba(0, 0, 0, 0.65)" },
      }}
    >
      <div className="relative flex h-full w-full flex-col bg-[#141414]">
        {/* Header: title + counter left, actions right. flex-1 + min-w-0 keeps the
            title from collapsing to zero when the actions row is wide (mobile). */}
        <div className="flex items-center gap-2 px-3 py-3 text-white sm:gap-3 sm:px-5 sm:py-3.5">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {/* The active item's own name wins, so paging a carousel updates the
                header. `title` is the fallback/context label for callers whose
                items carry no name. */}
            <span className="truncate text-sm font-semibold leading-none sm:text-[15px]">
              {current?.name || title || "Preview"}
            </span>
            {hasMultiple && (
              <span className="flex-shrink-0 rounded-full bg-white/10 px-2 py-1 text-xs font-medium leading-none text-white/70">
                {`${index + 1} / ${count}`}
              </span>
            )}
          </div>
          <div className="flex flex-shrink-0 items-center gap-1">
            {/* Desktop: toolbar lives in the header. Mobile/tablet: it floats over
                the media (see stage) so the name keeps its room here. */}
            <div className="mr-1 hidden lg:flex">{transformToolbar}</div>
            {showDownload &&
              current?.url &&
              (onDownload ? (
                // Caller owns the download (blob fetch, tracking, its own naming).
                <CustomButton
                  type="text"
                  shape="circle"
                  tooltipTitle={downloading ? "Downloading…" : "Download"}
                  icon={<LuDownload />}
                  loading={downloading}
                  className={TOOLBAR_BTN}
                  handleClick={handleDownloadClick}
                />
              ) : (
                <Tooltip title="Download">
                  <a
                    href={current.url}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Download"
                    className="flex items-center justify-center p-1 text-lg text-white/70 transition hover:text-white"
                  >
                    <LuDownload />
                  </a>
                </Tooltip>
              ))}
            <CustomButton
              type="text"
              shape="circle"
              tooltipTitle="Close"
              icon={<LuX />}
              className="flex items-center justify-center !border-0 !bg-transparent !text-xl !text-white/70 hover:!text-white"
              handleClick={onClose}
            />
          </div>
        </div>

        {/* Stage — media fills the remaining height, centered. */}
        <div
          ref={stageRef}
          className={cn(
            // overflow-hidden clips a zoomed image to the stage, so it can never
            // grow over the header / toolbar above.
            "relative flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-[#1c1c1e] px-4 pt-4",
            // Leave room at the bottom for the dots so they never sit on the media.
            hasMultiple ? "pb-10" : "pb-4"
          )}
          onWheel={handleWheel}
        >
          {renderSlide(
            current,
            autoPlay,
            transform,
            failure,
            markCurrentFailed,
            markCurrentLoaded,
            videoRef
          )}

          {/* Covers the gap between opening the lightbox and the full-size image
              being decoded, so a slow file reads as loading rather than broken. */}
          {isLoading && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <Spin />
            </div>
          )}

          {/* Floating toolbar — mobile / tablet only, pinned to the top. */}
          {transformToolbar && (
            <div className="absolute inset-x-0 top-3 z-20 flex justify-center lg:hidden">
              {transformToolbar}
            </div>
          )}

          {hasMultiple && (
            <>
              <CustomButton
                type="text"
                shape="circle"
                tooltipTitle="Previous"
                icon={<LuChevronLeft />}
                className={cn(NAV_BTN, "left-4")}
                handleClick={() => go(-1)}
              />
              <CustomButton
                type="text"
                shape="circle"
                tooltipTitle="Next"
                icon={<LuChevronRight />}
                className={cn(NAV_BTN, "right-4")}
                handleClick={() => go(1)}
              />

              {/* Dots — active slide is a wider bar; each dot jumps to its slide. */}
              <div className="absolute inset-x-0 bottom-3 z-20 flex items-center justify-center gap-1.5">
                {list.map((item, i) => (
                  <button
                    key={item.url}
                    type="button"
                    aria-label={`Go to item ${i + 1}`}
                    onClick={() => setIndex(i)}
                    className={cn(
                      "h-1.5 cursor-pointer rounded-full !border-0 p-0 outline-none transition-all duration-300",
                      i === index ? "w-5 !bg-white" : "w-1.5 !bg-white/50 hover:!bg-white/80"
                    )}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default MediaPreview;
